// Apply Edge — Pricing page. Port of design/source/public/pricing.html.
// Chrome (NavBar/Footer) is owned by HomeLayout — do not render here.
//
// AUP note: the restricted phrase from design HTML is replaced with
// "Apply Assist" in user-facing copy to satisfy Lemon Squeezy AUP
// (same swap as commit 0cf0c3bf).
import { msg, t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ArrowRightIcon, GiftIcon, PlusIcon } from "@phosphor-icons/react";
import { buildSoftwareApplicationJsonLd, comparePlans, getPlan } from "@reactive-resume/utils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { EgyptCardHint } from "@/client/components/billing/egypt-card-hint";
import { SEO } from "@/client/components/seo";
import { useAnalytics } from "@/client/hooks/use-analytics";
import { useLemonSqueezyOverlay } from "@/client/hooks/use-lemon-squeezy-overlay";
import {
  notifyCheckoutUnavailable,
  requestCheckoutUrl,
  type UpgradeTier,
} from "@/client/hooks/use-upgrade-checkout";
import { useUpgradePromptViewed } from "@/client/hooks/use-upgrade-prompt-viewed";
import { axios } from "@/client/libs/axios";
import { rememberCheckoutAttempt } from "@/client/services/billing/last-checkout-attempt";
import { useUser } from "@/client/services/user";

import { PricingCompareTable } from "./pricing-compare-table";
import {
  displayCredits,
  mergePricingCycleSearchParams,
  paidAmounts,
  type PricingCycle,
  resolvePricingCycle,
} from "./pricing-prices";
import { isActiveTrialist, type TrialTermsMode } from "./pricing-purchase-terms";
import schemaValuesJson from "./pricing-schema-values.json?raw";
import { type Tier, TierCard } from "./pricing-tier-card";

// [QA-K] Hardcoded VITE_LS_*_VARIANT_ID env values are LS internal DB IDs and
// produce 404s on the public checkout URL. Backend already returns the correct
// UUID slugs at /api/billing/config — prefer those when available, fall back
// to the env-baked URL only if the server config is unreachable.
type BillingConfig = {
  proCheckoutUrl?: string;
  proYearlyCheckoutUrl?: string;
  // Present only when the weekly LS checkout env is set.
  proWeeklyCheckoutUrl?: string;
  ultimateCheckoutUrl?: string;
  ultimateYearlyCheckoutUrl?: string;
};

// Weekly is a PRO-only billing cycle.
type Cycle = PricingCycle;
const fallbackLsProUrl = `https://apply-edge.lemonsqueezy.com/checkout/buy/${import.meta.env.VITE_LS_PRO_VARIANT_ID ?? ""}`;
const fallbackLsUltimateUrl = `https://apply-edge.lemonsqueezy.com/checkout/buy/${import.meta.env.VITE_LS_ULTIMATE_VARIANT_ID ?? ""}`;

const buildTiers = (proUrl: string, ultimateUrl: string): readonly Tier[] => {
  const free = getPlan("FREE");
  const pro = getPlan("PRO");
  const ultimate = getPlan("ULTIMATE");
  const proAmounts = paidAmounts("pro");
  const ultimateAmounts = paidAmounts("ultimate");
  // Credit pool sourced from PLANS — the number the server enforces.
  const proCredits = displayCredits("PRO", pro.quotas.autoApplications?.perMonth ?? 0);
  const ultimateCredits = displayCredits(
    "ULTIMATE",
    ultimate.quotas.autoApplications?.perMonth ?? 0,
  );
  return [
    {
      id: "pro",
      name: pro.name,
      tagline: t`For active candidates.`,
      ...proAmounts,
      cta: t`Start Pro`,
      ctaVariant: "primary",
      ctaHref: proUrl,
      note: t`Cancel anytime, no questions asked`,
      popular: true,
      // Surface the credit framing (BETA) where the highlights render.
      features: [...pro.marketingHighlights, t`${proCredits} Apply credits / month — BETA`],
    },
    {
      id: "ultimate",
      name: ultimate.name,
      tagline: t`For high-volume job seekers.`,
      ...ultimateAmounts,
      cta: t`Upgrade to Ultimate`,
      ctaVariant: "secondary",
      ctaHref: ultimateUrl,
      bestValue: true,
      note: t`Cancel anytime, no questions asked`,
      features: [
        ...ultimate.marketingHighlights.filter((item) => item.startsWith("Everything in")),
        ...ultimate.marketingHighlights.filter((item) => !item.startsWith("Everything in")),
        t`${ultimateCredits} Apply credits / month — BETA`,
      ],
      coming: t`One-click apply on major job boards`,
    },
    {
      id: "free",
      name: free.name,
      tagline: t`Everything you need to start.`,
      monthly: free.price.monthly,
      yearly: free.price.yearly,
      yearlyTotal: free.price.yearly,
      cta: t`Get started free`,
      ctaVariant: "ghost",
      ctaHref: "/auth/register",
      note: t`No card needed`,
      features: [...free.marketingHighlights],
    },
  ];
};

const COMPARE_GROUPS = comparePlans();

const FAQS = [
  {
    q: msg`Can I cancel anytime?`,
    a: msg`Yes. Cancel from Settings → Billing — your plan stays active until the end of the current period. No questions, no retention calls.`,
  },
  {
    q: msg`Do you offer a refund?`,
    a: msg`Yes. If Apply Edge isn't a fit within 14 days of your first paid charge, email refunds@apply-edge.com and we'll process a full refund. See the refund policy for details.`,
  },
  {
    q: msg`What happens to my data if I downgrade?`,
    a: msg`Nothing is deleted. If you exceed the new tier's caps, the oldest items are archived (read-only) and reactivate when you upgrade again.`,
  },
  {
    q: msg`Do you offer student or non-profit discounts?`,
    a: msg`Yes — write to support@apply-edge.com from a verified .edu or non-profit address. We typically discount Pro by 50%.`,
  },
  {
    q: msg`How do tailored applications get counted?`,
    a: msg`One application = one job + one tailored resume sent to that job. If the same role gets re-tailored, it doesn't double-count. Caps reset at 00:00 UTC.`,
  },
  {
    q: msg`Is there a team / company plan?`,
    a: msg`Not yet. We're focused on individual job seekers. Drop us a line if you're interested and we'll keep you posted.`,
  },
  {
    q: msg`Will my CV ever be sent to an employer without my approval?`,
    a: msg`Never. Apply Edge drafts and tailors. You hit Send.`,
  },
] as const;

/**
 * SoftwareApplication structured data, DERIVED from the PLANS map in
 * libs/utils/src/plans.ts — the same single source of truth the pricing
 * cards render from, so a price change can never leave the schema quoting
 * a stale figure.
 *
 * This is the block answer engines lift for "how much does Apply Edge
 * cost". Deliberately NO aggregateRating: Google requires it to reflect
 * genuine collected reviews, and inventing one is both a manual-action
 * risk and a lie. `offers` alone satisfies the rich-result requirement.
 */
const SCHEMA = JSON.parse(schemaValuesJson) as Record<string, string>;

const SOFTWARE_APPLICATION_LD = buildSoftwareApplicationJsonLd("/pricing");

/**
 * FAQPage for the pricing questions, derived from the same FAQS const the
 * page renders. Google requires FAQPage answers to be visible on the page;
 * deriving them makes drift impossible. See home-page.tsx for the same
 * pattern applied to the landing FAQ.
 */

export const PricingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useLingui();
  const faqs = FAQS.map(({ q, a }) => ({ q: i18n._(q), a: i18n._(a) }));
  const faqPageLd = {
    "@context": SCHEMA.context,
    "@type": SCHEMA.faqPage,
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": SCHEMA.question,
      name: q,
      acceptedAnswer: { "@type": SCHEMA.answer, text: a },
    })),
  };
  const { recordJourneyStep } = useAnalytics();
  const navigate = useNavigate();
  // Default (hint-gated) lookup: anonymous visitors make no /user/me request.
  const { user, loading: isAccountLoading, isError: isAccountError } = useUser();
  // Never block a checkout on the account lookup (it can hang or fail): while
  // the answer is unknown, a returning user gets the conservative terms line.
  // Same definition of "paid" as /billing/upgrade: a paid subscription id and not a trial.
  const isPayingSubscriber =
    user?.subscription?.status !== "TRIALING" && Boolean(user?.subscription?.lsSubscriptionId);
  let trialTerms: TrialTermsMode | null = null;
  if (isActiveTrialist(user?.subscription)) trialTerms = "trialist";
  else if (isAccountLoading || (isAccountError && !user)) trialTerms = "unknown";

  // Load lemon.js and route to the post-checkout return page when the overlay
  // reports success. Entitlements are granted by the billing webhook, not here.
  const { openHosted, isHandoffInFlight } = useLemonSqueezyOverlay({
    onSuccess: () => {
      // `success=true` is the app-controlled marker /billing/return requires
      // before it will poll /user/me — see use-upgrade-checkout.ts.
      void navigate("/billing/return?success=true");
    },
  });

  // GTM §6 funnel stage — fires once per mount, fire-and-forget in the same
  // shape as checkout_started. A 401 is the expected case for a logged-out
  // visitor (the funnel is per-user, and anonymous pricing traffic is a GA4
  // question), so failures are swallowed and must never affect the page.
  //
  // Without this stage, checkout_started sitting at 0 is ambiguous: it cannot
  // distinguish "nobody reaches pricing" from "people reach pricing and don't
  // click". Those need opposite fixes.
  useUpgradePromptViewed("pricing_page");
  useEffect(() => {
    void axios.post("/analytics/track", { event: "pricing_viewed" }).catch(() => null);
  }, []);

  // [QA-K] Resolve LS checkout URLs from the server (UUID slugs). Env-baked
  // numeric variant IDs return 404 on the public checkout endpoint.
  const { data: billingConfig } = useQuery<BillingConfig | null>({
    queryKey: ["billing-config"],
    queryFn: async () => {
      const { data } = await axios.get<BillingConfig | null>("/billing/config");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // The weekly PRO option appears only when a weekly checkout URL is
  // configured (NFR-004 graceful hide).
  const weeklyAvailable = Boolean(billingConfig?.proWeeklyCheckoutUrl);

  const cycle = resolvePricingCycle(searchParams.get("billing"), weeklyAvailable);

  const setCycle = (next: Cycle) => {
    setSearchParams(mergePricingCycleSearchParams(searchParams, next, weeklyAvailable));
  };

  let configuredProUrl = billingConfig?.proCheckoutUrl;
  if (cycle === "weekly") configuredProUrl = billingConfig?.proWeeklyCheckoutUrl;
  else if (cycle === "yearly") configuredProUrl = billingConfig?.proYearlyCheckoutUrl;
  const proUrl = configuredProUrl ?? fallbackLsProUrl;
  const ultimateUrl =
    (cycle === "yearly"
      ? billingConfig?.ultimateYearlyCheckoutUrl
      : billingConfig?.ultimateCheckoutUrl) ?? fallbackLsUltimateUrl;
  const TIERS = buildTiers(proUrl, ultimateUrl);
  const yearlySavingsPct = getPlan("PRO").price.savePct?.yearly ?? 25;
  const billingOptions: { id: Cycle; label: string; note?: string }[] = [
    ...(weeklyAvailable ? [{ id: "weekly" as const, label: t`Weekly` }] : []),
    { id: "monthly", label: t`Monthly` },
    { id: "yearly", label: t`Yearly`, note: t`Save ${yearlySavingsPct}%` },
  ];

  const handleCheckout = (tierId: Tier["id"]) => {
    if (tierId === "free") return;
    recordJourneyStep("checkout_initiated", {
      target_tier: tierId,
      billing_cycle: cycle,
      locale: i18n.locale,
    });
  };

  // Single-flight guard (Codex review, 2026-09-21): a ref, not just state — a
  // second click landing in the same tick as the first would still see a
  // stale `false` from `isRequestingCheckoutRef`'s state twin, which is
  // exactly how two `checkout_started` events / two overlays would happen.
  const isRequestingCheckoutRef = useRef(false);
  // The account can arrive while the checkout request is in flight (pricing
  // never blocks a checkout on the lookup); read it when the attempt is saved,
  // not when the button was clicked.
  const accountIdRef = useRef<string | undefined>();
  accountIdRef.current = user?.id;
  const [pendingCheckoutTierId, setPendingCheckoutTierId] = useState<"pro" | "ultimate" | null>(
    null,
  );

  const handleOpenCheckout = (
    checkoutUrl: string,
    tierId: "pro" | "ultimate",
    checkoutCycle: Cycle,
  ) => {
    if (isRequestingCheckoutRef.current || isHandoffInFlight?.()) return;
    isRequestingCheckoutRef.current = true;
    setPendingCheckoutTierId(tierId);
    // Bind the checkout to the exact variant the buyer picked — the static
    // `checkoutUrl` (LS ignores its `?variant=` param and defaults to
    // Monthly) is only a safe fallback for `checkoutCycle === "monthly"`;
    // see `requestCheckoutUrl`'s doc comment.
    const tier: UpgradeTier = tierId === "pro" ? "PRO" : "ULTIMATE";
    void requestCheckoutUrl(tier, checkoutCycle, checkoutUrl, "hosted", "pricing")
      .then((result) => {
        if (result.ok) {
          void Promise.resolve(
            openHosted(result.url, {
              surface: "pricing",
              plan: tier,
              interval: checkoutCycle,
              attempt_id: result.attemptId,
            }),
          ).then((opened) => {
            if (opened)
              rememberCheckoutAttempt({ tier, interval: checkoutCycle }, accountIdRef.current, {
                mode: "hosted",
                opened: true,
              });
          });
        } else {
          notifyCheckoutUnavailable(tier, checkoutCycle);
        }
      })
      .finally(() => {
        isRequestingCheckoutRef.current = false;
        setPendingCheckoutTierId(null);
      });
  };

  return (
    <div className="min-h-screen">
      {/*
        Was a bare <Helmet>: no canonical, no JSON-LD, and a RELATIVE og:image
        that social crawlers cannot resolve. <SEO> supplies all of it, and its
        defer={false} keeps the head write out of requestAnimationFrame so the
        SSG snapshot captures it — without that, /pricing shipped the SPA
        shell's homepage <title> to crawlers (observed live 2026-08-15).
      */}
      <SEO
        title={t`Pricing — Apply Edge`}
        description={t`Choose the right Apply Edge plan. Start free, upgrade to Pro or Ultimate for tailored applications and advanced AI features.`}
        canonical="/pricing"
        jsonLd={[SOFTWARE_APPLICATION_LD, faqPageLd]}
      />

      {/* Hero */}
      <section className="border-b-0 border-border sm:border-b">
        <div className="mx-auto max-w-[1280px] px-4 py-6 text-center sm:px-6 md:py-20">
          <span className="text-xs font-medium text-muted-foreground">
            <Trans>Pricing</Trans>
          </span>
          <h1 className="mt-4 text-[32px] font-semibold leading-[1.1] tracking-[-0.02em] text-foreground duration-700 animate-in fade-in slide-in-from-bottom-4 sm:text-[44px] md:text-[56px]">
            <Trans>Simple pricing.</Trans>
            <br />
            <span className="block font-semibold text-muted-foreground">
              <Trans>No surprises.</Trans>
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-[58ch] text-[15px] text-muted-foreground sm:mt-6">
            <Trans>
              Start free with 2 resumes and all templates. Upgrade when you&apos;re applying
              actively — pay only when Apply Edge is doing the work.
            </Trans>
          </p>

          {/* Billing toggle — the weekly tab appears only when a weekly PRO
              checkout is configured (NFR-004). */}
          <div
            className={`mx-auto mt-6 grid w-full max-w-sm gap-1 rounded-full border border-border bg-surface p-1 sm:mt-10 sm:inline-flex sm:w-auto ${billingOptions.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}
          >
            {billingOptions.map((opt) => {
              const active = cycle === opt.id;
              return (
                <button
                  key={opt.id}
                  aria-pressed={active}
                  type="button"
                  className={
                    "inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition sm:h-auto sm:px-5 sm:py-2 " +
                    (active
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground")
                  }
                  onClick={() => {
                    setCycle(opt.id);
                  }}
                >
                  {opt.label}
                  {opt.note && (
                    <span
                      className={
                        "hidden rounded-full px-2 py-0.5 text-xs font-medium sm:inline-flex " +
                        (active
                          ? "bg-background/15 text-background"
                          : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400")
                      }
                    >
                      {opt.note}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs font-medium text-emerald-700 sm:hidden">{t`Yearly saves ${yearlySavingsPct}%`}</p>
        </div>
      </section>

      {/* Tiers */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-4 py-4 sm:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-3">
            {TIERS.map((tier) => (
              <TierCard
                key={tier.id}
                className={
                  { free: "md:order-1", pro: "md:order-2", ultimate: "md:order-3" }[tier.id]
                }
                tier={tier}
                cycle={cycle}
                isCheckoutPending={pendingCheckoutTierId === tier.id}
                trialTerms={trialTerms}
                onCheckout={handleCheckout}
                onOpenCheckout={handleOpenCheckout}
              />
            ))}
          </div>
          {isPayingSubscriber ? null : (
            <EgyptCardHint className="mt-6 text-center text-[12.5px] text-muted-foreground" />
          )}
          {/* Apply credit + BETA framing. */}
          <div className="mt-6 space-y-1 text-center text-[12.5px] text-muted-foreground">
            <p>
              <Trans>
                Paid plans include monthly Apply credits — currently in BETA and improving fast.
              </Trans>
            </p>
            <p>
              <Trans>
                Prices and charges are in USD. Taxes are calculated at checkout; your bank may show
                a local-currency amount.
              </Trans>{" "}
              <Link to="/refund-policy" className="text-foreground underline underline-offset-2">
                <Trans>14-day full refund</Trans>
              </Link>{" "}
              <Trans>on first charge.</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* Compare table */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6 sm:py-16">
          <div className="mb-6 sm:mb-10">
            <span className="text-xs font-medium text-muted-foreground">
              <Trans>Compare</Trans>
            </span>
            <h2 className="mt-2 text-[22px] font-semibold leading-[1.15] tracking-tight text-foreground sm:text-[32px] sm:leading-tight">
              <Trans>Every feature, side by side</Trans>
            </h2>
          </div>

          <PricingCompareTable groups={COMPARE_GROUPS} />
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-6 sm:py-20">
          <div className="mb-10 text-center">
            <span className="text-xs font-medium text-muted-foreground">
              <Trans>FAQ</Trans>
            </span>
            <h2 className="mt-2 text-[32px] font-semibold tracking-tight text-foreground">
              <Trans>Pricing questions</Trans>
            </h2>
          </div>
          <div className="mx-auto max-w-[760px] divide-y divide-border rounded-xl border border-border bg-surface">
            {faqs.map((f) => (
              <details key={f.q} className="group">
                <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 text-[14.5px] font-medium text-foreground sm:px-6 sm:py-5">
                  {f.q}
                  <PlusIcon
                    aria-hidden
                    className="size-5 shrink-0 text-muted-foreground transition group-open:rotate-45"
                  />
                </summary>
                <div className="px-4 pb-5 text-[13.5px] leading-[1.6] text-muted-foreground sm:px-6">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Referral callout */}
      <section className="border-b border-border">
        <div className="mx-auto flex max-w-[1280px] items-start justify-center gap-4 px-4 py-6 sm:items-center sm:px-6 sm:py-10">
          <GiftIcon size={20} className="shrink-0 text-primary" />
          <p className="text-left text-[14.5px] text-muted-foreground sm:text-center">
            <Trans>
              Already a member?{" "}
              <Link
                to="/dashboard/referrals"
                className="font-medium text-primary underline underline-offset-2"
              >
                Refer a friend
              </Link>{" "}
              and earn 20% off your next billing cycle per signup.
            </Trans>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-6 sm:py-20">
          <div
            className="rounded-xl border border-border p-6 text-center sm:rounded-3xl sm:p-12"
            style={{
              background:
                "linear-gradient(180deg, hsl(var(--primary)/0.12), hsl(var(--background)))",
            }}
          >
            <h2 className="text-[22px] font-semibold leading-[1.15] tracking-tight text-foreground sm:text-[32px] md:text-[36px]">
              <Trans>Start free. Upgrade when you&apos;re applying.</Trans>
            </h2>
            <p className="mx-auto mt-3 max-w-[52ch] text-[14.5px] text-muted-foreground">
              <Trans>No card needed for Free. 14-day full refund on first paid charge.</Trans>
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
              <Link
                to="/auth/register"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-[13.5px] font-medium text-primary-foreground shadow-[0_6px_20px_-6px_hsl(var(--primary)/0.45)] hover:opacity-90 sm:h-auto sm:w-auto sm:rounded-full"
              >
                <Trans>Start free</Trans>
                <ArrowRightIcon size={14} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-6 py-3 text-[13.5px] font-medium text-foreground hover:bg-muted sm:h-auto sm:w-auto sm:rounded-full"
              >
                <Trans>Talk to us</Trans>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
